try { localStorage.setItem('RTV_DEBUG', '1'); } catch {}

export function extractJsonByBrackets(text) {
  let start = -1
  if (typeof text === 'object' ) {
    start = text.indexOf('{');
  }

  if (start === -1) return null;

  let jsonString = text.slice(start).trim();

  // Remove unescaped line breaks inside string values
  jsonString = jsonString.replace(/([^\\])"\s*:\s*"([^"]*?)\n([^"]*?)"/g, '$1": "$2 $3"');

  // Try to parse as-is
  try {
    return JSON.parse(jsonString);
  } catch (_) {}

  // Try trimming from both ends to find a parsable substring
  for (let left = 0; left < 20; left++) {
    for (let right = 0; right < 20; right++) {
      const attempt = jsonString.slice(left, jsonString.length - right).trim();

      // Fix unclosed quotes
      const quoteCount = (attempt.match(/"/g) || []).length;
      const fixedQuotes = quoteCount % 2 !== 0 ? attempt + '"' : attempt;

      // Fix unbalanced braces
      const openBraces = (fixedQuotes.match(/{/g) || []).length;
      const closeBraces = (fixedQuotes.match(/}/g) || []).length;
      const balanced = openBraces > closeBraces ? fixedQuotes + '}'.repeat(openBraces - closeBraces) : fixedQuotes;

      try {
        if (typeof responseText === 'string') {
          return JSON.parse(balanced);
        }
      } catch (_) {}
    }
  }

  // Try fixing unterminated "description" field
  const descMatch = jsonString.match(/"description"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)$/);
  if (descMatch) {
    const descStart = jsonString.indexOf(descMatch[0]);
    const before = jsonString.slice(0, descStart);
    const after = '"';
    const reconstructed = before + '"description": "' + descMatch[1] + after;

    const openBraces = (reconstructed.match(/{/g) || []).length;
    const closeBraces = (reconstructed.match(/}/g) || []).length;
    const balanced = openBraces > closeBraces ? reconstructed + '}'.repeat(openBraces - closeBraces) : reconstructed;

    try {
      return JSON.parse(balanced);
    } catch (_) {}
  }

  return null;
}

export function sanitizeColumnName(provider, model){
  return `${provider}_${model.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function toArray(x) {
  if (x == null) return [];
  return Array.isArray(x) ? x : [x];
}

function sortByTemplateIndex(arr) {
  return [...arr].sort((a, b) => {
    const ai = a.template_index ?? Number.POSITIVE_INFINITY;
    const bi = b.template_index ?? Number.POSITIVE_INFINITY;
    if (ai !== bi) return ai - bi;
    return 0;
  });
}

const RTV_DEBUG = (() => {
  try { return localStorage.getItem('RTV_DEBUG') === '1'; } catch { return false; }
})();
const dbg   = (...a) => { if (RTV_DEBUG) console.debug(...a); };
const warn  = (...a) => console.warn(...a);
const preview = (v, n = 300) => {
  try { return typeof v === 'string' ? v.slice(0, n) : JSON.stringify(v).slice(0, n); }
  catch { return String(v).slice(0, n); }
};

function normalizeStepLike(x, flag = 'unknown') {
  if (!x || typeof x !== 'object') {
    return { step: String(x ?? ''), note: '', doc_ref: undefined, type: '', approved: null, doctorsComment: '', template_index: undefined, _flag: flag };
  }
  const step = x.step ?? x.content ?? '';
  const note = x.note ?? x.reason ?? '';
  const type = x.type ?? '';
  const doc_ref = x.doc_ref ?? undefined;
  const approved = (x.approved ?? x.Approved ?? null);
  const doctorsComment = x.doctorsComment ?? x.DoctorsComment ?? '';
  const template_index = x.template_index ?? x.step_index ?? undefined;
  return { step, note, type, doc_ref, approved, doctorsComment, template_index, _flag: flag };
}

const safePreview = (x) => {
  try {
    if (x == null) return '';
    if (typeof x === 'string') return x.slice(0, 160);
    return JSON.stringify(x).slice(0, 160);
  } catch {
    return String(x);
  }
};

const safeString = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

function stripOuterQuotes(s) {
  if (typeof s !== 'string') return s;
  const trimmed = s.trim();
  if (
    trimmed.length >= 2 &&
    trimmed.startsWith('"') &&
    trimmed.endsWith('"')
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function buildOrderedSteps(comparison_result) {
  if (!comparison_result || typeof comparison_result !== 'object') {
    return [];
  }
  const matched = Array.isArray(comparison_result.matched_treatments)
    ? comparison_result.matched_treatments.map(item => ({
        kind: 'matched',
        step_index: item.step_index ?? null,
        template_index: item.template_index ?? null,
        content: safeString(item.content),
        doc_ref: safeString(item.doc_ref),
        type: item.type || '',
        matched_template_step: safeString(item.matched_template_step),
        probability: item.probability ?? null,
      }))
    : [];
  const unmatched = Array.isArray(comparison_result.unmatched_treatments)
    ? comparison_result.unmatched_treatments.map(item => ({
        kind: 'unmatched',
        step_index: item.step_index ?? null,
        template_index: null,
        content: safeString(item.content),
        doc_ref: safeString(item.doc_ref),
        type: item.type || '',
        reason: safeString(item.reason),
      }))
    : [];
  const timeline = [...matched, ...unmatched].sort((a, b) => {
    const ai = a.step_index ?? 999999;
    const bi = b.step_index ?? 999999;
    if (ai !== bi) return ai - bi;
    if (a.kind === b.kind) return 0;
    return a.kind === 'matched' ? -1 : 1;
  });
  const suggestedArr = Array.isArray(comparison_result.suggested_steps)
    ? comparison_result.suggested_steps.map(item => ({
        kind: 'suggested',
        step_index: null,
        template_index: item.template_index ?? null,
        content: safeString(item.content),
        doc_ref: safeString(item.doc_ref),
        type: item.type || '',
      }))
    : [];
  function findInsertionIndexForSuggested(tplIdx) {
    let leftPos = -1;
    let rightPos = timeline.length;
    for (let i = 0; i < timeline.length; i++) {
      const step = timeline[i];
      if (step.kind !== 'matched') continue;
      const tIndex = step.template_index;
      if (typeof tIndex === 'number') {
        if (tIndex < tplIdx) {
          if (i > leftPos) leftPos = i;
        }
        if (tIndex > tplIdx && rightPos === timeline.length) {
          rightPos = i;
        }
      }
    }
    if (leftPos === -1 && rightPos === timeline.length) {
      return timeline.length;
    }
    if (leftPos === -1) {
      return rightPos;
    }
    if (rightPos === timeline.length) {
      return leftPos + 1;
    }
    return rightPos;
  }
  suggestedArr.sort((a, b) => {
    const ta = a.template_index ?? 999999;
    const tb = b.template_index ?? 999999;
    return ta - tb;
  });
  for (const sug of suggestedArr) {
    const insertAt = findInsertionIndexForSuggested(
      Number.isFinite(sug.template_index) ? sug.template_index : 999999
    );
    timeline.splice(insertAt, 0, sug);
  }
  return timeline.map(item => {
    let prefix = '';
    if (item.kind === 'matched') {
      prefix = '✔ ';
    } else if (item.kind === 'unmatched') {
      prefix = '-> ';
    } else if (item.kind === 'suggested') {
      prefix = '➕ ';
    }
    const cleanContent = stripOuterQuotes(item.content || '');

    return {
      source: item.kind,
      step_index: item.step_index ?? null,
      template_index: item.template_index ?? null,
      step: prefix + cleanContent,
      doc_ref: item.doc_ref || '',
    };
  });
}

export function parseModelResponse(raw) {
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      obj = {};
    }
  }
  const extracted_title =
    obj?.extracted_title ??
    obj?.title ??
    obj?.Title ??
    obj?.titel ??
    '';
  const descField =
    obj?.description ??
    obj?.Description ??
    obj?.desc ??
    '';
  const comparison_result = obj?.comparison_result ?? obj ?? {};
  const treatment_steps = buildOrderedSteps(comparison_result);
  const cleanTitle = stripOuterQuotes(safeString(extracted_title));
  const cleanDesc  = stripOuterQuotes(safeString(descField));
  return {
    title: cleanTitle,
    description: cleanDesc,
    treatment_steps,
  };
}